import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationsListItem } from 'app/core/models/notification/get-all-notifications-by-user-id.model';
import { DynamicListConfig } from 'app/core/models/table/dynamic-list.model';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { ListColumnConfig } from 'app/main/components/list-page-config/list-page.config';
import { NotificationSignalRService } from 'app/main/shared/services/notification-signalr.service';
import { DynamicListComponent } from 'app/shared/components/dynamic-list/dynamic-list.component';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [DynamicListComponent, CommonModule, TranslateModule, FormsModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent implements OnInit {
  constructor(
    private notifHub: NotificationSignalRService,
    private notificationService: NotificationService,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.calculatePageSize();
    this.getList(this.currentPage, this.pageSize);
  }

  notificationConfig: DynamicListConfig = {
    pageTitleKey: 'notificationTable.notifications',
    columns: [
      {
        key: 'senderUserName',
        labelKey: 'notificationTable.senderUserName',
        type: 'user',
        width: 'minmax(0, 1.5fr)',
      },
      {
        key: 'title',
        labelKey: 'notificationTable.title',
        type: 'text',
        width: 'minmax(0, 1fr)',
      },
      {
        key: 'isRead',
        labelKey: 'notificationTable.isRead.label',
        type: 'text',
        width: 'minmax(0, 1.5fr)',
        transform: (value: boolean) => {
          return value
            ? this.translateService.instant('notificationTable.isRead.true')
            : this.translateService.instant('notificationTable.isRead.false');
        },
      },

      {
        key: 'createdDate',
        labelKey: 'notificationTable.createdDate',
        type: 'date',
        width: 'minmax(0, 0.8fr)',
      },
    ],
    actions: [],
  };

  notifications: NotificationsListItem[] = [];

  totalItems = 0;
  totalPage = 0;
  pageSize = 10;
  pageSizes = [10, 15, 20, 25, 50, 100];
  currentPage = 1;
  pageHeight!: number;

  isLoading = true;

  private calculatePageSize(reload: boolean = false): void {
    this.pageHeight = window.innerHeight;
    const newSize = this.getResponsivePageSizeFromCss();

    if (newSize !== this.pageSize) {
      this.pageSize = newSize;
      if (reload) {
        this.getList(this.currentPage, this.pageSize);
      }
    }
  }

  get gridTemplate(): string {
    return this.notificationConfig.columns
      .map((col) => `minmax(0, ${col.width})`)
      .join(' ');
  }

  getList(page: number, size: number): void {
    this.isLoading = true;
    const apiIndex = page - 1;

    this.notificationService
      .getAllNotificationsByUserId(apiIndex, size)
      .subscribe({
        next: (res) => {
          const data = res.data;

          if (data) {
            this.notifications = data.items;
            this.totalItems = data.count;
            this.totalPage = data.pages;
            this.currentPage = page;
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('getOrganizationList error', err);
          this.isLoading = false;
        },
      });
  }

  onCardClick(n: NotificationsListItem) {
    if (n.targetUrl && n.id) {
      this.notificationService
        .markNotificationsAsRead({
          id: n.id,
        })
        .subscribe((res) => {
          if (res.data.isSuccess) {
            this.notifHub.removeFromList(n.id);
            this.router.navigateByUrl(n.targetUrl);
          }
        });
    }
  }

  private getResponsivePageSizeFromCss(): number {
    const rootStyles = getComputedStyle(document.documentElement);

    const value = rootStyles.getPropertyValue('--user-page-size').trim();
    const parsed = Number(value);

    if (Number.isNaN(parsed) || parsed <= 0) {
      return this.pageSize || 10;
    }
    return parsed;
  }

  changePage(page: number): void {
    this.currentPage = page + 1;

    if (this.currentPage > 0 && this.currentPage <= this.totalPage) {
      this.getList(this.currentPage, this.pageSize);
    }
  }

  previousPage(page: number) {
    if (this.currentPage > 1) {
      this.getList(this.currentPage - 1, this.pageSize);
    }
  }

  nextPage(page: number) {
    if (this.currentPage < this.totalPage) {
      this.getList(this.currentPage + 1, this.pageSize);
    }
  }

  changePageSize(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.pageSize = Number(selectElement.value);
    this.currentPage = 1;
    this.getList(this.currentPage, this.pageSize);
  }

  getColClass(key?: string): string {
    switch (key) {
      case 'id':
        return 'col-id';
      case 'title':
        return 'col-title';
      case 'content':
        return 'col-content';
      case 'senderUserName':
        return 'col-sender';
      case 'isRead':
        return 'col-status';
      case 'createdDate':
        return 'col-date';
      default:
        return 'col-default';
    }
  }

  getReadStatus(isRead: boolean) {
    return isRead
      ? this.translateService.instant('notification.isRead.true')
      : this.translateService.instant('notification.isRead.false');
  }
}
